import React, { Component } from 'react';
import styled from 'styled-components/macro';
import { RouteComponentProps } from 'react-router-dom';
import { State as DiscountsState } from '../reducer';
import { GetDiscountsReason } from '../actions';
import { paramsEqual, updateParams } from '../../../../helpers/urlParams';
import DiscountsList from './discountsList';
import { LoadingOverlay } from '../../../../components/Loading';
import { getNextPageArgs } from '../helpers';
import Paging from './Paging';
import { Discount } from '../../../../types/discounts';
import DiscountDetails from './discountDetails';
import ListMessage from './ListMessage';

interface MatchParams {
    accountId: string;
    customerId: string;
}

interface DiscountsTabProps extends RouteComponentProps<MatchParams> {
    getDiscounts: (accountId: string, customer_id: string, params: URLSearchParams, origin: GetDiscountsReason) => void;
    setActiveDiscount: (discountId: string) => void;
    clearActiveDiscount: () => void;
    discounts: DiscountsState;
    activeDiscount?: Discount;
}

class DiscountsTab extends Component<DiscountsTabProps> {
    componentDidMount() {
        const { limit, accountId, id, discounts, params } = this.props.discounts;
        const urlAccountId = this.props.match.params.accountId;
        const urlId = this.props.match.params.customerId;
        // Fetch receipts on mount with initial query params
        const locationQueryParams = new URLSearchParams(this.props.location.search.substring(1));
        const requestQueryParams = updateParams(locationQueryParams, { limit: limit.toString() });
        const hasDiscounts = discounts.length > 0;
        const isSameAccount = accountId === urlAccountId;
        const isSameUser = id === urlId;
        const hasSameParams = paramsEqual(requestQueryParams, params, ['limit', 'starting_after']);
        const reason =
            hasDiscounts && isSameAccount && isSameUser && hasSameParams
                ? GetDiscountsReason.refresh
                : GetDiscountsReason.mount;

        this.props.getDiscounts(urlAccountId, urlId, requestQueryParams, reason);
    }

    componentDidUpdate(prevProps: DiscountsTabProps, prevState: Readonly<{}>) {
        // Query params have changed, fetch Receipts again...
        if (this.props.location.search !== prevProps.location.search) {
            const { limit } = this.props.discounts;
            const urlAccountId = this.props.match.params.accountId;
            const urlId = this.props.match.params.customerId;
            const locationQueryParams = new URLSearchParams(this.props.location.search.substring(1));
            const requestQueryParams = updateParams(locationQueryParams, { limit: limit.toString() });
            this.props.getDiscounts(urlAccountId, urlId, requestQueryParams, GetDiscountsReason.update);
        }
    }

    render() {
        const { getDiscounts, setActiveDiscount, clearActiveDiscount, activeDiscount } = this.props;
        const { discounts, isLoading, hasNext, isLoadingNext } = this.props.discounts;
        const accountId = this.props.match.params.accountId;
        const customerId = this.props.match.params.customerId;
        const limit = this.props.discounts.limit.toString();
        const locationQueryParams = new URLSearchParams(this.props.location.search.substring(1));
        const nextPageArgs = getNextPageArgs(discounts);
        const nextParams = updateParams(locationQueryParams, { ...nextPageArgs, limit });
        const next = () => {
            getDiscounts(accountId, customerId, nextParams, GetDiscountsReason.next);
        };
        return (
            <>
                <OuterContainer>
                    <Wrapper>
                        <DiscountsList
                            discounts={discounts}
                            setActiveDiscount={setActiveDiscount}
                        />
                        <RelativeGroup>
                            <Paging next={next} isLoading={isLoading} hasNext={hasNext}/>
                            <ListMessage
                                discounts={discounts}
                                accountId={accountId}
                                isLoading={isLoading}
                                customerId={customerId}
                                hasNext={hasNext}
                                isLoadingNext={isLoadingNext}
                            />
                            {isLoading && <LoadingOverlay/>}
                        </RelativeGroup>
                    </Wrapper>
                </OuterContainer>
                {activeDiscount && (
                    <DiscountDetails discount={activeDiscount} clearActiveDiscount={clearActiveDiscount}/>
                )}
            </>
        );
    }
}

const OuterContainer = styled.div`
    background: #fff;
`;

const Wrapper = styled.div`
    position: relative;
    box-sizing: border-box;
`;

const RelativeGroup = styled.div`
    position: relative;
    box-sizing: border-box;
`;

export default DiscountsTab;
